import type {
  AdminJobPage,
  AdminJobQuery,
  AdminProjectionFreshness,
  AdminQueryMeta,
} from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import {
  buildAdminAiRuntimeSummaryViewModel,
  type AdminAiRuntimeSummaryViewModel,
} from "../../shared/locale/admin-ai-runtime-summary";
import {
  buildAdminObservabilityAggregateViewModel,
  type AdminObservabilityAggregateViewModel,
} from "../../shared/locale/admin-observability-aggregate-summary";
import {
  buildAdminJobRowSnapshotViewModel,
  type AdminJobRowSnapshotViewModel,
} from "../../shared/locale/admin-job-row-snapshot-summary";
import {
  buildAdminAccountDeletionSeveritySummary,
  type AdminAccountDeletionSeveritySummaryViewModel,
} from "../../shared/locale/admin-account-deletion-severity-summary";
import {
  formatAdminJobHotspotHeadline,
  formatAdminJobHotspotSummary,
} from "../../shared/locale/admin-job-hotspot-copy";
import { formatAdminJobStatusLabel } from "../../shared/locale/admin-dashboard-status-copy";
import { formatAdminCount } from "../../shared/formatting/format-admin-count";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";
import { formatAdminDuration } from "../../shared/formatting/format-admin-duration";
import { formatAdminSourceKindLabel } from "../../shared/formatting/format-admin-source-kind";
import { summarizeJobBacklog } from "./jobs-use-cases";

export type JobMonitorViewModel = {
  accountDeletionSeverity?: AdminAccountDeletionSeveritySummaryViewModel;
  aiRuntime?: AdminAiRuntimeSummaryViewModel;
  observability?: AdminObservabilityAggregateViewModel;
  rowSnapshot?: AdminJobRowSnapshotViewModel;
  generatedAtLabel: string;
  requestId: string;
  nextCursor?: string;
  freshness: AdminProjectionFreshness;
  sourceFreshness: Array<{
    source: string;
    freshness: AdminProjectionFreshness;
    sourceKind?: string;
    sourceKindLabel?: string;
  }>;
  hotspot: {
    detail: string;
    drilldownTo: string;
    headline: string;
    jobTypeLabel: string;
  };
  backlogSummary: {
    totalCountLabel: string;
    queuedCountLabel: string;
    processingCountLabel: string;
    failedCountLabel: string;
  };
  rows: Array<{
    jobId: string;
    jobType: string;
    status: string;
    attemptCount: number;
    queuedAt?: string;
    startedAt?: string;
    finishedAt?: string;
    jobTypeLabel: string;
    priorityLabel: string;
    ownerUserIdHashLabel: string;
    petIdHashLabel: string;
    statusLabel: string;
    attemptCountLabel: string;
    lastErrorCodeLabel: string;
    providerLabel: string;
    durationLabel: string;
    queuedAtLabel: string;
    startedAtLabel: string;
    finishedAtLabel: string;
    linkedFileIdLabel: string;
    linkedRecordIdLabel: string;
    actionSlotLabel: string;
  }>;
};

function toLabel(value: string | undefined): string {
  return value ?? "-";
}

function buildSearchParams(
  query: AdminJobQuery,
  preserveExplicitUpperBound: boolean,
): string {
  const params = new URLSearchParams();

  params.set("from", query.timeRange.from);
  if (preserveExplicitUpperBound) {
    params.set("to", query.timeRange.to);
  }

  for (const [key, value] of Object.entries({
    jobType: query.jobType,
    status: query.status,
    userIdHash: query.userIdHash,
    petIdHash: query.petIdHash,
    linkedFileId: query.linkedFileId,
    linkedRecordId: query.linkedRecordId,
    requestId: query.requestId,
    correlationId: query.correlationId,
    release: query.release,
    appVersion: query.appVersion,
  })) {
    if (value && value.length > 0) {
      params.set(key, value);
    }
  }

  const queryString = params.toString();
  return queryString.length > 0 ? `?${queryString}` : "";
}

function toFreshness(meta: AdminQueryMeta): AdminProjectionFreshness {
  return Object.values(meta.sourceFreshness).includes("stale")
    ? "stale"
    : Object.values(meta.sourceFreshness).includes("partial") ||
        Object.values(meta.sourceFreshness).includes("unavailable")
      ? "partial"
      : "fresh";
}

function buildSourceFreshness(
  meta: AdminQueryMeta,
  locale: AdminDashboardLocale,
): JobMonitorViewModel["sourceFreshness"] {
  return Object.entries(meta.sourceFreshness).map(([source, freshness]) => ({
    source,
    freshness,
    sourceKind: meta.sourceKinds?.[source],
    sourceKindLabel: formatAdminSourceKindLabel(meta.sourceKinds?.[source], locale),
  }));
}

function compareJobRows(left: AdminJobPage["items"][number], right: AdminJobPage["items"][number]): number {
  const leftPriority = left.jobType === "account_deletion" && left.status === "failed"
    ? 0
    : left.jobType === "account_deletion" && (left.status === "processing" || left.status === "queued")
      ? 1
      : left.status === "failed"
        ? 2
        : left.status === "processing" || left.status === "queued"
          ? 3
          : left.jobType === "account_deletion"
            ? 4
            : 5;
  const rightPriority = right.jobType === "account_deletion" && right.status === "failed"
    ? 0
    : right.jobType === "account_deletion" && (right.status === "processing" || right.status === "queued")
      ? 1
      : right.status === "failed"
        ? 2
        : right.status === "processing" || right.status === "queued"
          ? 3
          : right.jobType === "account_deletion"
            ? 4
            : 5;

  if (leftPriority !== rightPriority) {
    return leftPriority - rightPriority;
  }

  const leftQueuedAt = left.queuedAt ? Date.parse(left.queuedAt) : Number.NaN;
  const rightQueuedAt = right.queuedAt ? Date.parse(right.queuedAt) : Number.NaN;

  if (!Number.isNaN(leftQueuedAt) && !Number.isNaN(rightQueuedAt) && rightQueuedAt !== leftQueuedAt) {
    return rightQueuedAt - leftQueuedAt;
  }

  return left.jobId.localeCompare(right.jobId);
}

function buildPriorityLabel(
  job: AdminJobPage["items"][number],
  locale: AdminDashboardLocale,
): string {
  if (job.jobType === "account_deletion" && job.status === "failed") {
    return pickAdminDashboardLocaleText(locale, { en: "Deletion failed", ko: "삭제 실패" });
  }

  if (job.jobType === "account_deletion" && (job.status === "processing" || job.status === "queued")) {
    return pickAdminDashboardLocaleText(locale, { en: "Deletion backlog", ko: "삭제 대기" });
  }

  if (job.status === "failed") {
    return pickAdminDashboardLocaleText(locale, { en: "Failed job", ko: "실패 작업" });
  }

  if (job.status === "processing" || job.status === "queued") {
    return pickAdminDashboardLocaleText(locale, { en: "Backlog", ko: "대기" });
  }

  return pickAdminDashboardLocaleText(locale, { en: "Stable", ko: "안정" });
}

function buildHotspot(
  jobs: AdminJobPage["items"],
  query: AdminJobQuery,
  preserveExplicitUpperBound: boolean,
  locale: AdminDashboardLocale,
): JobMonitorViewModel["hotspot"] {
  const topJob = [...jobs].sort(compareJobRows)[0];

  if (!topJob) {
    return {
      headline: pickAdminDashboardLocaleText(locale, { en: "Stable jobs", ko: "안정 작업" }),
      jobTypeLabel: "-",
      detail: pickAdminDashboardLocaleText(locale, {
        en: "There is no job hotspot that currently needs operator attention.",
        ko: "현재 주의가 필요한 작업 핫스팟이 없습니다.",
      }),
      drilldownTo: `/admin/jobs${buildSearchParams(query, preserveExplicitUpperBound)}`,
    };
  }

  const sameTypeJobs = jobs.filter((job) => job.jobType === topJob.jobType);
  const failedCount = sameTypeJobs.filter((job) => job.status === "failed").length;
  const processingCount = sameTypeJobs.filter((job) => job.status === "processing").length;
  const queuedCount = sameTypeJobs.filter((job) => job.status === "queued").length;
  const succeededCount = sameTypeJobs.filter((job) => job.status === "succeeded").length;

  return {
    headline: formatAdminJobHotspotHeadline({
      jobType: topJob.jobType,
      failedCount,
      processingCount,
      queuedCount,
      succeededCount,
    }, locale),
    jobTypeLabel: topJob.jobType,
    detail: formatAdminJobHotspotSummary({
      jobType: topJob.jobType,
      failedCount,
      processingCount,
      queuedCount,
      succeededCount,
    }, locale),
    drilldownTo: `/admin/jobs${buildSearchParams({
      ...query,
      jobType: topJob.jobType,
    }, preserveExplicitUpperBound)}`,
  };
}

function buildAccountDeletionSeverity(
  jobs: AdminJobPage["items"],
  query: AdminJobQuery,
  preserveExplicitUpperBound: boolean,
  locale: AdminDashboardLocale,
): AdminAccountDeletionSeveritySummaryViewModel | undefined {
  const accountDeletionJobs = jobs.filter((job) => job.jobType === "account_deletion");

  if (accountDeletionJobs.length === 0) {
    return undefined;
  }

  return buildAdminAccountDeletionSeveritySummary({
    failedCount: accountDeletionJobs.filter((job) => job.status === "failed").length,
    processingCount: accountDeletionJobs.filter((job) => job.status === "processing").length,
    queuedCount: accountDeletionJobs.filter((job) => job.status === "queued").length,
    succeededCount: accountDeletionJobs.filter((job) => job.status === "succeeded").length,
  }, `/admin/jobs${buildSearchParams({
    ...query,
    jobType: "account_deletion",
  }, preserveExplicitUpperBound)}`, locale);
}

export function toJobMonitorViewModel(
  page: AdminJobPage,
  meta: AdminQueryMeta,
  query: AdminJobQuery,
  preserveExplicitUpperBound = false,
  locale: AdminDashboardLocale = "ko",
): JobMonitorViewModel {
  const backlogSummary = summarizeJobBacklog(page.items);
  const sortedRows = [...page.items].sort(compareJobRows);

  return {
    accountDeletionSeverity: buildAccountDeletionSeverity(page.items, query, preserveExplicitUpperBound, locale),
    aiRuntime: buildAdminAiRuntimeSummaryViewModel(page.aiRuntimeOperations, locale, [
      "aiProviderRouteLabel",
      "aiQuotaState",
      "aiProviderLatencyBucket",
      "aiServingAccessState",
      "manualReviewFallbackPressure",
      "aiModelUsageSummary",
      "aiModelLimitSummary",
    ]),
    observability: buildAdminObservabilityAggregateViewModel(page.observabilityAggregate, locale, [
      "oldestQueuedAgeBucket",
      "jobRetryPressure",
      "terminalFailureCodeDistribution",
      "providerFailureDistribution",
    ]),
    rowSnapshot: buildAdminJobRowSnapshotViewModel(page.items, meta.generatedAt, locale),
    generatedAtLabel: formatAdminDate(meta.generatedAt),
    requestId: meta.requestId,
    nextCursor: meta.nextCursor,
    freshness: toFreshness(meta),
    sourceFreshness: buildSourceFreshness(meta, locale),
    hotspot: buildHotspot(page.items, query, preserveExplicitUpperBound, locale),
    backlogSummary: {
      totalCountLabel: formatAdminCount(page.totalCount ?? backlogSummary.totalCount),
      queuedCountLabel: formatAdminCount(backlogSummary.queuedCount),
      processingCountLabel: formatAdminCount(backlogSummary.processingCount),
      failedCountLabel: formatAdminCount(backlogSummary.failedCount),
    },
    rows: sortedRows.map((job) => ({
      jobId: job.jobId,
      jobType: job.jobType,
      status: job.status,
      attemptCount: job.attemptCount,
      queuedAt: job.queuedAt,
      startedAt: job.startedAt,
      finishedAt: job.finishedAt,
      jobTypeLabel: job.jobType,
      priorityLabel: buildPriorityLabel(job, locale),
      ownerUserIdHashLabel: toLabel(job.ownerUserIdHash),
      petIdHashLabel: toLabel(job.petIdHash),
      statusLabel: formatAdminJobStatusLabel(job.status, locale),
      attemptCountLabel: formatAdminCount(job.attemptCount),
      lastErrorCodeLabel: toLabel(job.lastErrorCode),
      providerLabel: toLabel(job.provider),
      durationLabel: formatAdminDuration(job.durationMs),
      queuedAtLabel: formatAdminDate(job.queuedAt),
      startedAtLabel: formatAdminDate(job.startedAt),
      finishedAtLabel: formatAdminDate(job.finishedAt),
      linkedFileIdLabel: toLabel(job.linkedFileId),
      linkedRecordIdLabel: toLabel(job.linkedRecordId),
      actionSlotLabel: pickAdminDashboardLocaleText(locale, { en: "View details", ko: "상세 보기" }),
    })),
  };
}
